import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { z } from 'zod';

import { loadRuntimeConfig } from '@/config/runtime-config';
import { installChunkErrorHandler } from '@/lib/chunk-error-handler';
import '@/index.css';

// Install chunk error handler early — auto-reloads the page when a
// dynamically imported module fails due to a stale deployment (#857).
installChunkErrorHandler();

// Belt-and-suspenders: the real fix is the inline <script> in index.html
// that pre-populates globalThis.__zod_globalConfig before ESM modules load.
// This call is kept as a safety net in case index.html is ever changed, but
// by itself it runs too late — ESM import hoisting causes z.object() schema
// creation in imported modules to execute before this statement (#931).
z.config({ jitless: true });

async function startApp(): Promise<void> {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw new Error('Root element not found');
  }

  // Load runtime config before rendering anything.
  // In dev mode this returns hardcoded defaults (no fetch).
  await loadRuntimeConfig();

  if (import.meta.env.DEV) {
    // Lazy-import dev modules to keep them out of production builds
    const { worker } = await import('@/mocks/browser');
    const { chatStreamHandler } = await import('@/mocks/mock-chat-stream');
    worker.use(chatStreamHandler);
    await worker.start({ onUnhandledRequest: 'bypass' });
    const { DevApp } = await import('@/dev/dev-app');
    createRoot(rootEl).render(
      <StrictMode>
        <DevApp />
      </StrictMode>,
    );
  } else {
    const { App } = await import('@/app');
    createRoot(rootEl).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  }
}

void startApp();
